import React from 'react';
import { AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

/**
 * Enhanced validation error display component
 * Handles both simple error strings and detailed validation errors from the API
 */
const ValidationErrorDisplay = ({ 
  error, 
  onDismiss,
  title = "Configuration Error",
  type = "error", // error, warning, info
  className = ""
}) => {
  // Handle different error formats
  const getErrorDetails = () => {
    if (!error) return null;

    // String error
    if (typeof error === 'string') {
      return {
        summary: error,
        details: null
      };
    }

    // Enhanced API error with validation details
    if (error.errors && Array.isArray(error.errors)) {
      return {
        summary: error.summary || "Configuration validation failed",
        details: error.errors
      };
    }

    // Basic object error
    if (error.message) {
      return {
        summary: error.message,
        details: null
      };
    }

    // Fallback
    return {
      summary: JSON.stringify(error),
      details: null
    };
  };

  const errorInfo = getErrorDetails();
  if (!errorInfo) return null;

  const icons = {
    error: AlertCircle,
    warning: AlertTriangle,
    info: Info
  };

  const colors = {
    error: {
      bg: "bg-red-50 border-red-200",
      icon: "text-red-500",
      title: "text-red-800",
      text: "text-red-700",
      button: "text-red-400 hover:text-red-600"
    },
    warning: {
      bg: "bg-yellow-50 border-yellow-200",
      icon: "text-yellow-500",
      title: "text-yellow-800",
      text: "text-yellow-700",
      button: "text-yellow-400 hover:text-yellow-600"
    },
    info: {
      bg: "bg-blue-50 border-blue-200",
      icon: "text-blue-500",
      title: "text-blue-800",
      text: "text-blue-700",
      button: "text-blue-400 hover:text-blue-600"
    }
  };

  const Icon = icons[type];
  const colorScheme = colors[type];

  return (
    <div className={`border rounded-lg p-4 ${colorScheme.bg} ${className}`}>
      <div className="flex items-start">
        <div className={`flex-shrink-0 ${colorScheme.icon} mr-3 mt-0.5`}>
          <Icon className="h-5 w-5" />
        </div>
        
        <div className="flex-grow min-w-0">
          <div className="flex items-start justify-between">
            <div>
              <h3 className={`text-sm font-medium ${colorScheme.title}`}>
                {title}
              </h3>
              <p className={`mt-1 text-sm ${colorScheme.text}`}>
                {errorInfo.summary}
              </p>
            </div>
            
            {onDismiss && (
              <button
                onClick={onDismiss}
                className={`flex-shrink-0 ml-3 ${colorScheme.button} transition-colors`}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Detailed validation errors */}
          {errorInfo.details && errorInfo.details.length > 0 && (
            <div className="mt-3">
              <h4 className={`text-xs font-medium ${colorScheme.title} uppercase tracking-wide mb-2`}>
                Validation Details
              </h4>
              <div className="space-y-2">
                {errorInfo.details.map((detail, index) => (
                  <div key={index} className="text-xs">
                    <div className="flex items-start space-x-2">
                      <span className={`font-medium ${colorScheme.title} min-w-0`}>
                        {detail.fieldName || detail.path}:
                      </span>
                      <span className={`${colorScheme.text}`}>
                        {detail.message}
                      </span>
                    </div>
                    
                    {/* Additional context for developers */}
                    {(detail.received || detail.expected) && (
                      <div className={`mt-1 ml-2 text-xs ${colorScheme.text} opacity-75`}>
                        {detail.received && (
                          <div>Received: <code className="bg-black bg-opacity-10 px-1 rounded">{JSON.stringify(detail.received)}</code></div>
                        )}
                        {detail.expected && (
                          <div>Expected: <code className="bg-black bg-opacity-10 px-1 rounded">{JSON.stringify(detail.expected)}</code></div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValidationErrorDisplay;